import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AdminLayout } from "../layouts";
import {
  Auth,
  Users,
  Menu,
  Roles,
  Permissions,
  Dashboard,
  Home,
  // Reports,
  Companies,
  Integrations,
  Recover,
  Sites,
  NewSiteForm,
  LegalRequirements,
  AnnualPlan,
  Locations,
  Causes,
  Employees,
  Records,
  Countries,
  Areas,
  Activities,
  Divisions,
  Sources,
  Frequencies,
  Industries,
  Requirements,
  Authorities,
  Evidences,
  EvidenceSearch,
  Responsibles,
  AccidentTypes,
  Sectors,
  Accidents,
  Privacidad,
  Terminos,
  ActionPlans,
  WorkPermits,
  WorkPermitView,
  Capacitaciones,
  Topics,
  RiskAssessments,
  RiskTypes,
  PotentialDamages,
  Ocupaciones,
  ContractTypes,
  WorkEnvironments,
  AccidentMechanisms,
  Deviations,
  MaterialAgents,
  WorkProcesses,
  InjuryNatures,
  SelfAssessments,
  OcdeGuide,
  AuditMap,
  DashboardDD,
  DDHome,
  EPP,
  FlaAssessment,
  Bibliography,
  Repository,
  OcdeRiskRegister,
  OcdeCap,
  OcdeDdProcess,
  PolicyTracker,
  Checklist,
  InspectRun,
  CiiuActivities,
} from "../pages/admin";
import { useAuth } from "../hooks";

import Reset from "../pages/admin/Reset/Reset";
import { InviteComplete } from "../pages/admin/Invite";

// Ruta no reconocida estando sin sesión: guarda el destino (ej. un link de QR
// a /inspect/:id) para retomarlo después del login, y manda a la landing.
function CapturedRedirect() {
  const location = useLocation();

  useEffect(() => {
    const target = location.pathname + location.search;
    if (target !== "/admin") {
      sessionStorage.setItem("postLoginRedirect", target);
    }
  }, [location]);

  return <Navigate to="/admin" replace />;
}

export function AdminRouter() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Si había un destino pendiente (ej. QR escaneado sin sesión activa),
  // se lee acá — de forma síncrona con el render — para que la ruta "/admin"
  // no dispare su redirect a home por defecto y compita con este.
  const pendingRedirect = user ? sessionStorage.getItem("postLoginRedirect") : null;

  useEffect(() => {
    if (!pendingRedirect) return;
    sessionStorage.removeItem("postLoginRedirect");
    navigate(pendingRedirect, { replace: true });
  }, [pendingRedirect, navigate]);

  const loadLayout = (Layout, Page) => {
    return (
      <Layout>
        <Page />
      </Layout>
    );
  };

  return (
    <Routes>
      {/* Rutas públicas sin autenticación */}
      <Route path="/invite/:token" element={<InviteComplete />} />
      <Route path="/privacidad" element={<Privacidad />} />
      <Route path="/terminos" element={<Terminos />} />

      {!user ? (
        <>
          <Route path="/admin/*" element={<Auth />} />
          <Route path="/recover" element={<Recover />} />
          <Route path="/reset/:token" element={<Reset />} />
          <Route path="*" element={<CapturedRedirect />} />
        </>
      ) : (
        <>
          <Route path="/admin" element={pendingRedirect ? null : <Navigate to="/admin/home" replace />} />
          <Route path="/admin/home"      element={loadLayout(AdminLayout, Home)} />
          <Route path="/admin/dashboard" element={loadLayout(AdminLayout, Dashboard)} />
          <Route path="/admin/users" element={loadLayout(AdminLayout, Users)} />
          <Route path="/admin/config/roles" element={loadLayout(AdminLayout, Roles)} />
          <Route path="/admin/config/permissions" element={loadLayout(AdminLayout, Permissions)} />
          <Route path="/admin/menu" element={loadLayout(AdminLayout, Menu)} />
          {/* <Route path="/admin/reports" element={loadLayout(AdminLayout, Reports)} /> */}
          <Route path="/admin/companies" element={loadLayout(AdminLayout, Companies)} />
          {/* Integraciones: la página misma solo se muestra al master */}
          <Route path="/admin/integrations" element={loadLayout(AdminLayout, Integrations)} />
          <Route path="/admin/sites" element={loadLayout(AdminLayout, Sites)} />
          <Route path="/admin/siteforms/newsiteform" element={loadLayout(AdminLayout, NewSiteForm)} />
          <Route path="/admin/legal-requirements" element={loadLayout(AdminLayout, LegalRequirements)} />
          <Route path="/admin/annual-plan" element={loadLayout(AdminLayout, AnnualPlan)} />
          <Route path="/admin/locations"  element={loadLayout(AdminLayout, Locations)} />
          <Route path="/admin/causes"     element={loadLayout(AdminLayout, Causes)} />
          <Route path="/admin/employees"  element={loadLayout(AdminLayout, Employees)} />
          <Route path="/admin/records"    element={loadLayout(AdminLayout, Records)} />
          <Route path="/admin/countries" element={loadLayout(AdminLayout, Countries)} />
          <Route path="/admin/areas" element={loadLayout(AdminLayout, Areas)} />
          <Route path="/admin/activities" element={loadLayout(AdminLayout, Activities)} />
          <Route path="/admin/divisions" element={loadLayout(AdminLayout, Divisions)} />
          <Route path="/admin/sources" element={loadLayout(AdminLayout, Sources)} />
          <Route path="/admin/frequencies" element={loadLayout(AdminLayout, Frequencies)} />
          <Route path="/admin/industries" element={loadLayout(AdminLayout, Industries)} />
          <Route path="/admin/requirements" element={loadLayout(AdminLayout, Requirements)} />
          <Route path="/admin/authorities" element={loadLayout(AdminLayout, Authorities)} />
          <Route path="/admin/evidences" element={loadLayout(AdminLayout, Evidences)} />
          <Route path="/admin/evidence-search" element={loadLayout(AdminLayout, EvidenceSearch)} />
          <Route path="/admin/responsibles" element={loadLayout(AdminLayout, Responsibles)} />
          <Route path="/admin/accidents" element={loadLayout(AdminLayout, Accidents)} />
          <Route path="/admin/accident-types" element={loadLayout(AdminLayout, AccidentTypes)} />
          <Route path="/admin/sectors" element={loadLayout(AdminLayout, Sectors)} />
          <Route path="/admin/action-plans" element={loadLayout(AdminLayout, ActionPlans)} />
          <Route path="/admin/work-permits"     element={loadLayout(AdminLayout, WorkPermits)} />
          <Route path="/admin/checklist" element={loadLayout(AdminLayout, Checklist)} />
          <Route path="/inspect/:id" element={<InspectRun />} />
          <Route path="/admin/work-permits/:id" element={loadLayout(AdminLayout, WorkPermitView)} />
          <Route path="/admin/epp"          element={loadLayout(AdminLayout, EPP)} />
          <Route path="/admin/capacitaciones" element={loadLayout(AdminLayout, Capacitaciones)} />
          <Route path="/admin/training-topics" element={loadLayout(AdminLayout, Topics)} />
          <Route path="/admin/eval-riesgos" element={loadLayout(AdminLayout, RiskAssessments)} />
          <Route path="/admin/risk-types" element={loadLayout(AdminLayout, RiskTypes)} />
          <Route path="/admin/potential-damages" element={loadLayout(AdminLayout, PotentialDamages)} />
          <Route path="/admin/occupations" element={loadLayout(AdminLayout, Ocupaciones)} />
          <Route path="/admin/contract-types" element={loadLayout(AdminLayout, ContractTypes)} />
          <Route path="/admin/work-environments"    element={loadLayout(AdminLayout, WorkEnvironments)} />
          <Route path="/admin/accident-mechanisms" element={loadLayout(AdminLayout, AccidentMechanisms)} />
          <Route path="/admin/deviations"          element={loadLayout(AdminLayout, Deviations)} />
          <Route path="/admin/material-agents"     element={loadLayout(AdminLayout, MaterialAgents)} />
          <Route path="/admin/work-processes"       element={loadLayout(AdminLayout, WorkProcesses)} />
          <Route path="/admin/injury-natures"       element={loadLayout(AdminLayout, InjuryNatures)} />
          <Route path="/admin/self-assessments"     element={loadLayout(AdminLayout, SelfAssessments)} />
          <Route path="/admin/ocde-guide"           element={loadLayout(AdminLayout, OcdeGuide)} />
          <Route path="/admin/audit-map"            element={loadLayout(AdminLayout, AuditMap)} />
          <Route path="/admin/dd-home"               element={loadLayout(AdminLayout, DDHome)} />
          <Route path="/admin/dashboard-dd"         element={loadLayout(AdminLayout, DashboardDD)} />
          <Route path="/admin/fla-assessment"       element={loadLayout(AdminLayout, FlaAssessment)} />
          <Route path="/admin/bibliography"          element={loadLayout(AdminLayout, Bibliography)} />
          <Route path="/admin/repository"            element={loadLayout(AdminLayout, Repository)} />
          <Route path="/admin/ocde-risk-register"    element={loadLayout(AdminLayout, OcdeRiskRegister)} />
          <Route path="/admin/ocde-cap"              element={loadLayout(AdminLayout, OcdeCap)} />
          <Route path="/admin/ocde-dd-process"       element={loadLayout(AdminLayout, OcdeDdProcess)} />
          <Route path="/admin/policy-tracker"        element={loadLayout(AdminLayout, PolicyTracker)} />
          <Route path="/admin/ciiu-activities"      element={loadLayout(AdminLayout, CiiuActivities)} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </>
      )}
    </Routes>
  );
}
